// Story 8.9 (ADO-2018), AC2 — clear the cart, confirmed first. The trigger renders
// only when the cart has lines (SL1.0 renders Erase Cart only for a non-empty
// cart, `get_cart.php:1428-1434`). The host says whether it has: the basket store
// holds the shared cart as last read (Story 8.3, ADO-2012), so /cart passes what its
// review lists (a line added since that read included, AC1 and Gate Decision 1), and a
// host that passes nothing gets the store's lines (the side panel, which shows only
// those). It
// opens the existing confirm-dialog pattern (`Modal` + a `secondary` Cancel and a
// `destructive` confirm, the MyAccount erasure precedent) with the Gate Decision 5
// copy. Cancel, Escape and the backdrop close it without a request; only the
// confirm sends the clear, which the store runs
// non-optimistically, so the lines stay until the server has cleared them. Once the
// clear is in flight the dialog cannot be dismissed (it will land regardless), so
// Cancel is blocked with it, and the confirm is blocked while any line edit or
// previous-order add is still pending (the store refuses). Both are blocked with `aria-disabled`, not the
// native attribute: a browser drops focus to <body> when the focused control turns
// `disabled`, which would let Escape reach the host panel and Tab leave the modal.
//
// A confirmed clear closes the dialog, whose focus restore returns focus to the
// trigger: either detached in that same commit (focus drops to <body>) or, on
// /cart, still mounted until the review's re-read drops it. So the host passes
// `onCleared` to move focus to a stable target of its own (the panel heading, the
// /cart page title), and it runs after the commit that closes the dialog, so the
// restore cannot override it.
//
// Mounted in the `CartSidePanel` header's second row as a `link` at every width (Figma
// 5330:16161 and 5381:17372) and in the /cart page actions as a `button` (4978:2234), reading
// "Clear cart" in both.
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Button, Icon, Modal } from '../design-system';
import { useBasketStore } from './useBasketStore';
import styles from './ClearCartControl.module.css';

export interface ClearCartControlProps {
  /** `link`: the side panel's text trigger (trash 14px + label). `button`: a
   *  destructive Button (/cart page actions). */
  readonly appearance: 'link' | 'button';
  /** Trigger label (default "Clear cart"). */
  readonly label?: string;
  /** Whether the cart has lines, which shows the trigger. Defaults to the basket
   *  store's lines (the shared cart as last read); /cart passes its review's, which
   *  may hold a line added since. */
  readonly hasLines?: boolean;
  /** Called once a confirmed clear has succeeded and the dialog has closed, so the
   *  host moves focus to a stable target (PR #560 review). */
  readonly onCleared?: () => void;
}

/** Keep Tab and Escape inside the confirm dialog: React bubbles its events up the
 *  component tree, so without this a host dialog (the side panel's own focus trap)
 *  would also handle them after the Modal did. Other keys bubble as usual. */
function stopAtTheDialog(e: KeyboardEvent<HTMLElement>): void {
  if (e.key === 'Tab' || e.key === 'Escape') e.stopPropagation();
}

export function ClearCartControl({
  appearance,
  label = 'Clear cart',
  hasLines: hostHasLines,
  onCleared,
}: ClearCartControlProps) {
  const storeHasLines = useBasketStore((s) => s.lines.length > 0);
  const hasLines = hostHasLines ?? storeHasLines;
  const clearing = useBasketStore((s) => s.clearing);
  const clearError = useBasketStore((s) => s.clearError);
  // Story 8.3 (ADO-2012), card 06 (D-21): the last clear was refused because the cart changed
  // since it was opened; the dialog then asks again with the counts now.
  const storeClearChanged = useBasketStore((s) => s.clearChanged);
  const dismissClearChanged = useBasketStore((s) => s.dismissClearChanged);
  const clear = useBasketStore((s) => s.clear);
  // A line write or a previous-order add in flight makes the store refuse the clear.
  const basketWritePending = useBasketStore((s) => s.pending.size > 0 || s.addingFromOrder);
  const [open, setOpen] = useState(false);
  // Show a failure only for a confirm made in THIS opening of the dialog.
  const [attempted, setAttempted] = useState(false);
  // Only a refusal of this opening's own confirm turns it into card 06: the store's refusal may
  // be another dialog's (the side panel's and the page's share it), or land after this one was
  // reopened, and must not change a dialog that asked nothing yet.
  const clearChanged = attempted ? storeClearChanged : null;
  // Bumped by each successful clear. Its own state change forces a commit even when
  // the dialog had already gone (the store emptied first), and the effect runs after
  // the dialog's focus restore, which React runs first as an effect cleanup.
  const [clears, setClears] = useState(0);
  const handledClears = useRef(0);
  useEffect(() => {
    if (clears === handledClears.current) return;
    handledClears.current = clears;
    onCleared?.();
  }, [clears, onCleared]);

  if (!hasLines) {
    // The basket emptied under an open dialog (a depot switch superseding an
    // in-flight clear resets it): drop the dialog state too, so this same instance
    // does not reopen the dialog over the next cart once it gains a line. Adjusting
    // state during render is React's pattern for resetting on a changed input.
    if (open || attempted) {
      setOpen(false);
      setAttempted(false);
    }
    return null;
  }

  const openDialog = () => {
    setAttempted(false);
    dismissClearChanged();
    setOpen(true);
  };
  // A confirmed clear is already on its way: hiding the dialog would let the basket
  // empty with no visible confirmation, so every dismissal path waits for it.
  const close = () => {
    if (clearing) return;
    setOpen(false);
    // Cancel after a refusal only closes the dialog (D-21).
    dismissClearChanged();
  };
  const confirmBlocked = clearing || basketWritePending;
  const confirm = async () => {
    if (confirmBlocked) return;
    setAttempted(true);
    if (await clear()) {
      setOpen(false);
      setClears((count) => count + 1);
    }
  };

  return (
    <>
      {appearance === 'link' ? (
        <button type="button" className={styles.link} onClick={openDialog}>
          <Icon name="trash" size={14} />
          {label}
        </button>
      ) : (
        <Button variant="destructive" onClick={openDialog}>
          {label}
        </Button>
      )}
      {/* A wrapper with no box of its own: the Modal portals to <body>. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <span className={styles.dialogHost} onKeyDown={stopAtTheDialog}>
        <Modal
          open={open}
          onClose={close}
          title={clearChanged === null ? 'Clear cart?' : 'Your cart has changed'}
          footer={
            <>
              <Button variant="secondary" onClick={close} aria-disabled={clearing || undefined}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirm}
                aria-disabled={confirmBlocked || undefined}
              >
                Clear cart
              </Button>
            </>
          }
        >
          {clearChanged === null ? (
            <p className={styles.body}>
              Are you sure you want to delete ALL the items in your cart?
            </p>
          ) : (
            // Story 8.3 (ADO-2012), card 06: the frames' copy, word for word (DV-3). An alert, so
            // the switch from the usual question is announced (a changed title alone is not).
            <p className={styles.body} role="alert">
              {`This cart changed since you opened it. It now has ${counted(clearChanged.lineCount, 'line')} and ${counted(clearChanged.caseCount, 'case')}. Clear it anyway? This can't be undone.`}
            </p>
          )}
          {attempted && clearError !== null ? (
            <p role="alert" className={styles.error}>
              {clearError}
            </p>
          ) : null}
        </Modal>
      </span>
    </>
  );
}

/** "1 line" / "N lines", "1 case" / "N cases": card 06's counts. */
function counted(count: number, noun: 'line' | 'case'): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}
